// OWNED BY the broadcast implementation agent (see ../BroadcastTab.tsx ownership note).
//
// React binding for the contact's broadcast send records: renders instantly
// from the session cache (warmed by preloadBroadcastTab on contacts-table row
// hover / page mount), then keeps the list LIVE via light equality-filtered
// onSnapshot listeners that write back into the same cache key (so the
// tab-strip badge updates too). The tab stays mounted after first visit — the
// listeners register once per contact id and clean up when the page unmounts.

import { useEffect } from "react";
import { useSessionCacheValue } from "../../data/sessionCache";
import {
  BROADCAST_MAX_AGE_MS,
  contactBroadcastKey,
  fetchSentMessagesForContact,
  subscribeToContactSentMessages
} from "./broadcastData";
import type {
  BulkMessageSentMessage,
  RealEstateContact
} from "../../../../../../@types/sharedSchema";

type ContactSentMessagesState = {
  messages: BulkMessageSentMessage[] | undefined;
  loading: boolean;
  error: Error | null;
};

export function useContactSentMessages(contact: RealEstateContact): ContactSentMessagesState {
  const contactId = contact?.id ?? "";

  const { data, loading, error } = useSessionCacheValue<BulkMessageSentMessage[]>(
    contactBroadcastKey(contactId),
    () => fetchSentMessagesForContact(contact),
    { maxAgeMs: BROADCAST_MAX_AGE_MS }
  );

  useEffect(() => {
    if (!contactId) return undefined;
    return subscribeToContactSentMessages(contact);
    // Candidates derive from id + callerPhoneNumber only — the id is the identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  return {
    messages: data,
    // "loading" only on a true cold start — a warm cache renders instantly.
    loading: loading && data === undefined,
    error
  };
}
